import { useState, useEffect } from "react";
import { Quote, PenLine } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { ReviewModal } from "../components/ReviewModal";
import { CTA } from "./Home";
import { IMAGES } from "../data/content";
import { fetchReviews } from "../lib/api";

export default function Reviews() {
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    fetchReviews().then(setReviews).catch(() => setReviews([]));
  }, []);

  const published = (reviews || []).map((review) => ({
    id: review.id,
    quote: review.text,
    author: review.name,
    role: review.suburb,
  }));

  return (
    <div data-testid="reviews-page">
      <PageHero
        testid="reviews-hero"
        eyebrow="Reviews"
        title="Kind words from families."
        subtitle="The families we support are at the heart of everything we do. Read their experiences — and, if we've supported your child, we'd love to hear yours."
        image={IMAGES.reviews}
      />

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex justify-center">
          <button data-testid="leave-review-btn" onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-sora-blue text-white rounded-[20px] px-8 py-4 font-bold hover:bg-sora-slate transition-colors shadow-sm">
            <PenLine size={18} /> Leave a Review
          </button>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl min-h-[180px] mx-auto px-6 md:px-10">
          {reviews === null ? (
            <p className="text-center text-sora-charcoal/60" aria-live="polite">Loading reviews...</p>
          ) : published.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {published.map((review, index) => (
                <Reveal key={review.id} delay={(index % 3) * 0.08}>
                  <div data-testid={`review-card-${index}`} className="rounded-[24px] bg-sora-warm border border-sora-stone/25 p-9 h-full shadow-[0_8px_30px_rgba(63,88,115,0.05)]">
                    <Quote size={34} className="text-sora-sage" />
                    <p className="mt-5 font-display text-xl text-sora-slate leading-relaxed italic">"{review.quote}"</p>
                    <div className="mt-6 pt-5 border-t border-sora-stone/30">
                      <p className="font-bold text-sora-slate">{review.author}</p>
                      {review.role && <p className="text-sm text-sora-charcoal/60">{review.role}</p>}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="text-center rounded-[20px] bg-sora-warm border border-sora-stone/25 px-6 py-10">
              <p className="font-display text-xl text-sora-slate">No published reviews yet.</p>
            </div>
          )}
        </div>
      </section>

      <CTA />
      <ReviewModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
